<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Użytkownicy</span>
        <h3 class="page-title">Lista użytkowników</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success"
          @click.prevent="$router.push('/uzytkownicy/nowy')">
          <i class="material-icons">add</i>
          Dodaj użytkownika
        </d-button>
      </d-col>
    </d-row>

    <d-row
      v-if="loaded && currentUserID > 0"
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="users-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          placeholder="Podaj imię, nazwisko lub numer osobowy użytkownika"
          v-model="filters.search" />

        <v-select
          style="width: 360px;"
          class="ml-2"
          :options="accountTypeOptions"
          v-model="filters.accountType"
          placeholder="Rodzaj konta"
          :searchable="false"
          label="text">
        </v-select>

        <v-select
          style="width: 360px;"
          class="ml-2"
          :options="accountActiveOptions"
          v-model="filters.accountActive"
          :searchable="false"
          placeholder="Konto aktywne?"
          label="text">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">Imię i nazwisko</th>
            <th scope="col" class="border-0">Numer osobowy</th>
            <th scope="col" class="border-0">Rodzaj konta</th>
            <th scope="col" class="border-0 text-center">Aktualne zamówienia</th>
            <th scope="col" class="border-0">Konto aktywne?</th>
            <th scope="col" class="border-0">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded && currentUserID">
          <tr
            v-for="(user, index) of users"
            :key="'user-' + index">
            <td class="align-middle">
              {{ user.name }}
            </td>
            <td class="align-middle">
              <span v-if="user.type === 'WORKER'">
                {{ user.personal_number }}
              </span>
              <span v-else>
                &mdash;
              </span>
            </td>
            <td class="align-middle">
              <strong
                v-if="user.type === 'ADMIN'"
                class="font-weight-bold text-primary">
                Administrator
              </strong>
              <span v-if="user.type === 'WORKER'">
                Pracownik
              </span>

              <br v-if="user.is_pattern || user.is_seamstress">
              <small v-if="user.is_pattern || user.is_seamstress">
                <template v-if="user.is_pattern">Wykrajanie</template>
                <template v-if="user.is_pattern && user.is_seamstress">, </template>
                <template v-if="user.is_seamstress">Szycie</template>
              </small>
            </td>
            <td class="align-middle text-center">
              <d-badge
                v-if="user.type === 'WORKER'"
                :theme="user.current_orders_count > 0 ? 'success' : 'secondary'"
                @click.native="openOrdersPopup(user.id)">
                {{ user.current_orders_count }}
              </d-badge>
              <span v-else>&mdash;</span>
            </td>
            <td class="align-middle">
              <span
                v-if="user.active === 1"
                class="text-success">
                Aktywne
              </span>
              <span
                v-if="user.active === 0"
                class="text-danger">
                Nieaktywne
              </span>
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/uzytkownicy/edycja/' + user.id)"
                class="mr-2"
                title="Edytuj">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="user.id === currentUserID || removeInProgress"
                @click.native="removeUser(user.id)"
                title="Usuń">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="users.length === 0">
            <td
              colspan="5"
              class="text-center">
              Brak użytkowników do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded || !currentUserID" class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />

    <d-modal
      v-if="ordersPopup.visible"
      size="lg"
      class="big-popup"
      @close="handleOrdersPopupClose">
      <d-modal-header>
        <d-modal-title>
          Zamówienia pracownika
        </d-modal-title>
      </d-modal-header>
      <d-modal-body>
        <orders
          :inPopup="true"
          :workerID="ordersPopup.workerID" />
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Orders from './../views/Orders.vue';
import Pagination from './../components/common/Pagination.vue';
import Vue from 'vue';

export default {
  name: 'users',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'orders': Orders,
    'pagination': Pagination
  },
  computed: {
    accountTypeOptions () {
      return [
        {
          text: 'Dowolny',
          value: ''
        },
        {
          text: 'Administrator',
          value: 'ADMIN'
        },
        {
          text: 'Pracownik',
          value: 'WORKER'
        }
      ];
    },
    accountActiveOptions () {
      return [
        {
          text: 'Konto aktywne',
          value: 1
        },
        {
          text: 'Konta nieaktywne',
          value: 0
        }
      ];
    }
  },
  data() {
    return {
      users: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      currentUserID: 0,
      ordersPopup: {
        visible: false,
        workerID: false
      },
      filters: {
        search: '',
        accountType: null,
        accountActive: null
      }
    };
  },
  mounted () {
    this.currentUserID = this.$store.getters.getUserID;
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search
      };

      if (this.filters.accountType !== null && this.filters.accountType && this.filters.accountType.value) {
        where.type = this.filters.accountType.value;
      }

      if (this.filters.accountActive !== null && this.filters.accountActive && typeof this.filters.accountActive.value === 'number') {
        where.active = this.filters.accountActive.value;
      }

      ListUtils.loadItemsData(this, {
        endpoint: '/users/items?page=' + this.currentPage,
        listField: 'users',
        pagination: true,
        params: {
          where
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        accountType: null,
        accountActive: null
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['users-filters-form'], this.debouncedLoadFilteredData);
    },
    removeUser (userID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/users/delete/',
        id: userID,
        successTitle: 'Usunięto użytkownika',
        successText: 'Wybrany użytkownik został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tego użytkownika?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie użytkownika nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    openOrdersPopup (workerID) {
      setTimeout(() => {
        Vue.set(this.ordersPopup, 'visible', true);
        this.ordersPopup.workerID = workerID;
      }, 0);
    },
    handleOrdersPopupClose () {
      Vue.set(this.ordersPopup, 'visible', false);
    }
  }
};
</script>

